import { useState, useCallback } from "react";
import { InvoiceDataEntity, ProcessInvoiceJsonUseCase } from "../../domain";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";

// Create repository instance (singleton)
const invoiceRepository = new InvoiceRepositoryImpl();

// Create use case
const processInvoiceJsonUseCase = new ProcessInvoiceJsonUseCase(
  invoiceRepository
);

export interface UseInvoiceProcessingReturn {
  processing: boolean;
  error: string | null;
  processInvoiceJson: (invoiceData: InvoiceDataEntity) => Promise<boolean>;
  clearError: () => void;
}

export const useInvoiceProcessing = (): UseInvoiceProcessingReturn => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Process invoice JSON for GL classification
   */
  const processInvoiceJson = useCallback(
    async (invoiceData: InvoiceDataEntity): Promise<boolean> => {
      setProcessing(true);
      setError(null);

      try {
        const result = await processInvoiceJsonUseCase.execute(invoiceData);

        if (!result.success) {
          throw new Error(result.error || "Failed to process invoice");
        }

        return result.success;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to process invoice";
        setError(errorMessage);
        console.error("Failed to process invoice JSON:", error);
        return false;
      } finally {
        setProcessing(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    processing,
    error,
    processInvoiceJson,
    clearError,
  };
};
